import {
  getUserRoleSalesOrderRelatedPermissionGroupAssignments,
  PermissionsGroupName,
  UserPermissions,
} from '@zspace/roles';
import { SalesOrder, User, UserRoleAssignment } from '@zspace/types';
import { useCallback, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaPenToSquare } from 'react-icons/fa6';
import SalesOrdersList from '../../sales-orders/sales-orders-list/sales-orders-list';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import useIsUserEditable from '../../shared/hooks/is-user-editable';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import Button from '../../ui/button/button';
import InfiniteTable, {
  CustomInfiniteTableProps,
} from '../../ui/table/infinite-table/infinite-table';
import {
  Column,
  ExpandableRow,
  OnTableChangeFunction,
  RowInteraction,
} from '../../ui/table/types';
import { UserStatusText } from '../types';
import { PermissionsGroupBySalesOrder } from '../users-table/users-table';
import styles from './users-summary-table.module.scss';

type InfiniteTableProps = CustomInfiniteTableProps<User>;

export type UsersSummaryTableProps = InfiniteTableProps & {
  onChange: OnTableChangeFunction<User, PermissionsGroupBySalesOrder>;
  relatedSalesOrderId: string;
  handleUserTemporaryPermissionsClick?: (user: User) => void;
  adminViewMode?: boolean;
};

export function UsersSummaryTable({
  dataSource,
  onChange,
  config,
  fetchMore,
  hasMore,
  loadingInitialData,
  empty,
  handleUserTemporaryPermissionsClick,
  adminViewMode,
  relatedSalesOrderId,
}: UsersSummaryTableProps) {
  const { user: currentUser } = useUser();
  const { isEditableBySalesOrderRelatedUser } = useIsUserEditable();
  const [, setSelectedUser] = useState<User | undefined>(undefined);
  const [activeRowId, setActiveRowId] = useState<string>('');

  const getUserPermissionsGroup = useCallback(
    (user: User): PermissionsGroupName | null => {
      let userPermissionsGroup: PermissionsGroupName | null = null;
      let userRole: UserRoleAssignment | undefined;

      if (adminViewMode) {
        userRole = user.roles.find((userRoleAssignment) =>
          userRoleAssignment.permissionsGroupUserRoleAssignments.find(
            (permissionGroupAssignment) =>
              permissionGroupAssignment.salesOrder?.id === relatedSalesOrderId
          )
        );
      } else {
        userRole = user.roles.find(
          (userRoleAssignment) =>
            userRoleAssignment.role.name === currentUser.activeRole?.role.name
        );
      }

      if (userRole) {
        const permissionsGroupUserRoleAssignment =
          userRole.permissionsGroupUserRoleAssignments[0];

        if (permissionsGroupUserRoleAssignment) {
          userPermissionsGroup = permissionsGroupUserRoleAssignment
            .permissionsGroup.name as PermissionsGroupName;
        }
      }

      return userPermissionsGroup;
    },
    [adminViewMode, currentUser.activeRole?.role.name, relatedSalesOrderId]
  );

  const isExpandableRow = useCallback(
    (row: User) => {
      const userRoles = new Set<PermissionsGroupName>();
      row.roles.forEach((userRoleAssignment) => {
        userRoleAssignment.permissionsGroupUserRoleAssignments.forEach(
          (permissionsGroupUserRoleAssignment) => {
            if (
              !adminViewMode &&
              userRoleAssignment.role.name !== currentUser.activeRole?.role.name
            ) {
              return;
            }
            userRoles.add(
              permissionsGroupUserRoleAssignment.permissionsGroup
                .name as PermissionsGroupName
            );
          }
        );
      });
      return userRoles.size > 1;
    },
    [adminViewMode, currentUser.activeRole?.role.name]
  );

  const rowInteraction: RowInteraction = {
    onRowEnter: function (rowKey: string): void {
      setActiveRowId(rowKey);
    },
    onRowLeave: function (): void {
      setActiveRowId('');
    },
  };

  const columns: Column<User, PermissionsGroupBySalesOrder>[] = useMemo(
    () => [
      {
        key: 'email',
        className: 'is-width-25',
        render: (el) => (
          <Element display="flex" alignItems="center">
            <span>{el.email}</span>
            <CheckPermissions permissions={UserPermissions.USERS_ASSIGN_ROLES}>
              <CheckPermissions.Render>
                <If condition={isEditableBySalesOrderRelatedUser(el)}>
                  <Button
                    ml={1}
                    text
                    className={`has-text-primary-dark is-size-7 ${
                      el.id === activeRowId ? '' : `${styles.invisible}`
                    }`}
                    onClick={() => {
                      setSelectedUser(el);
                      handleUserTemporaryPermissionsClick?.(el);
                    }}
                  >
                    <Icon>
                      <FaPenToSquare />
                    </Icon>
                    <span>Edit details</span>
                  </Button>
                </If>
              </CheckPermissions.Render>
            </CheckPermissions>
          </Element>
        ),
        title: 'User',
        sortable: true,
      },
      {
        key: 'role',
        className: 'is-width-20',
        render: (el) => {
          const permissionsGroup = getUserPermissionsGroup(el);
          if (isExpandableRow(el) || !permissionsGroup) {
            return null;
          }

          return <span>{permissionsGroup}</span>;
        },
        title: 'Role',
        expandableContentRender: (el) => el.permissionsGroupName,
      },
      {
        key: 'salesOrders',
        className: 'is-width-35',
        render: (el) => {
          if (isExpandableRow(el)) {
            return null;
          }
          let roles = el.roles;
          if (!adminViewMode) {
            roles = roles.filter(
              (roleAssignment) =>
                roleAssignment.role.name === currentUser.activeRole?.role.name
            );
          }
          const parsedSalesOrdersSet = roles.reduce((acc, roleAssignment) => {
            roleAssignment.permissionsGroupUserRoleAssignments.forEach(
              (permissionsGroupUserRoleAssignment) => {
                if (permissionsGroupUserRoleAssignment.salesOrder) {
                  acc.add(permissionsGroupUserRoleAssignment.salesOrder);
                }
              }
            );
            return acc;
          }, new Set<SalesOrder>());

          const parsedSalesOrders = Array.from(parsedSalesOrdersSet.values());
          const link = `/users/${el.id}`;

          return (
            <SalesOrdersList
              list={parsedSalesOrders}
              textColor="primary"
              hasMoreLink={link}
              openLinksInNewTab
            />
          );
        },
        title: 'Sales orders',
        sortable: false,
        expandableContentRender: (el, parentElement) => {
          if (!el.salesOrders) {
            return null;
          }

          const salesOrders = el.salesOrders;

          const link = `/users/${parentElement.id}`;

          return (
            <SalesOrdersList
              list={salesOrders}
              textColor="primary"
              hasMoreLink={link}
              openLinksInNewTab
            />
          );
        },
      },
      {
        key: 'deletedAt',
        className: 'is-width-15',
        render: (el) => {
          const userStatus = el.lastActive
            ? UserStatusText.ACTIVE
            : UserStatusText.PENDING_FIRST_LOGIN;

          return <span>{userStatus}</span>;
        },
        title: 'Status',
        sortable: false,
      },
    ],
    [
      isEditableBySalesOrderRelatedUser,
      activeRowId,
      handleUserTemporaryPermissionsClick,
      isExpandableRow,
      getUserPermissionsGroup,
      adminViewMode,
      currentUser.activeRole?.role.name,
    ]
  );

  const expandableRow = useMemo(():
    | ExpandableRow<User, PermissionsGroupBySalesOrder>
    | undefined => {
    return {
      expandable: (row: User) => isExpandableRow(row),
      expandableRowIterator: (row: User) => {
        const permissionsGroupsBySalesOrderMap = new Map<
          PermissionsGroupName,
          PermissionsGroupBySalesOrder
        >();

        let userRoleAssignments = row.roles;

        if (!adminViewMode) {
          userRoleAssignments = userRoleAssignments.filter(
            (assignment) =>
              assignment.role.name === currentUser.activeRole?.role.name
          );
          if (userRoleAssignments.length === 0) {
            return [];
          }
        }

        const userSalesOrderRelatedPermissionGroupsAssignments =
          userRoleAssignments.flatMap((assignment) =>
            getUserRoleSalesOrderRelatedPermissionGroupAssignments(assignment)
          );

        if (!userSalesOrderRelatedPermissionGroupsAssignments) {
          return [];
        }

        userSalesOrderRelatedPermissionGroupsAssignments.forEach(
          (assignment) => {
            const permissionsGroupName = assignment.permissionsGroup
              .name as PermissionsGroupName;

            const existingGroup =
              permissionsGroupsBySalesOrderMap.get(permissionsGroupName);
            const salesOrders = existingGroup
              ? [...existingGroup.salesOrders, assignment.salesOrder!]
              : [assignment.salesOrder!];

            permissionsGroupsBySalesOrderMap.set(permissionsGroupName, {
              permissionsGroupName,
              salesOrders,
            });
          }
        );

        return Array.from(permissionsGroupsBySalesOrderMap.values());
      },
    };
  }, [adminViewMode, isExpandableRow, currentUser.activeRole?.role.name]);

  return (
    <InfiniteTable
      fetchMore={fetchMore}
      hasMore={hasMore}
      columns={columns}
      rowKey="id"
      loadingInitialData={loadingInitialData}
      config={config}
      dataSource={dataSource}
      onChange={onChange}
      empty={empty}
      expandableRow={expandableRow}
      rowInteraction={rowInteraction}
    />
  );
}

export default UsersSummaryTable;
